import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Image, Form, Button } from "react-bootstrap";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/images/coyote-logo.jpg";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import auth2 from "../../../assets/images/auth/02.png";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import FocusError from "../../../components/error/error-focus";
import InputField from "../../../components/input/input-field";

const RecoverPassword = () => {
  let navigate = useNavigate();
  const alertRef = useRef(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const [errorPassword, setErrorPassword] = useState([]);
  const [errorConfirmPassword, setErrorConfirmPassword] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!email || !token) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: "Invalid URL",
        type: "danger",
        show: true,
        alertFor: "recover",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  }, [email, token]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const response = await axios.put(`${url.backendURL}user/reset-password`, {
        password: values.password,
        email,
        token,
      });
      setErrorConfirmPassword([]);
      setErrorPassword([]);
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "recover",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate("/user/auth/sign-in");
      }, 2000);
    } catch (err) {
      const { response } = err;
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor: "recover",
      });
      setErrorConfirmPassword(response.data.error?.confirmPassword);
      setErrorPassword(response.data.error?.password);
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const passwordValidationSchema = Yup.object().shape({
    password: Yup.string()
    .min(6, "Password must be at least 6 characters long")
    .max(30, "Password cannot be longer than 30 characters")
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{6,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    )
    .required("Password is required"), 
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords do not match")
      .required("Confirm password is required"),
  });

  return (
    <>
      <section className="login-content">
        <Row className="m-0 align-items-center bg-white vh-100">
          <Col
            md="6"
            className="d-md-block d-none bg-primary p-0 mt-n1 vh-100 overflow-hidden"
          >
            <Image
              src={auth2}
              className="img-fluid gradient-main animated-scaleX"
              alt="images"
            />
          </Col>
          <Col md="6" className="p-0" ref={alertRef}>
            <Card className="card-transparent auth-card shadow-none d-flex justify-content-center mb-0">
              <Card.Body>
                <AlertNotificationSimple
                  type={alert.type}
                  message={alert.message}
                  show={alert.show}
                  onClose={() => setAlert({ ...alert, show: false })}
                />
                <Link
                  to="/user/auth/sign-in"
                  className="navbar-brand d-flex align-items-center mb-3"
                >
                  <img
                    className="logo-title text-center mb-2 coyote-logo-image h-auto w-25"
                    src={logo}
                    alt="coyote-logo"
                  />
                </Link>
                <h2 className="mb-2">Reset Password</h2>
                <p>Enter your new password to reset your account password.</p>
                <Formik
                  initialValues={{
                    ...formData,
                  }}
                  validationSchema={passwordValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    values,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form>
                      <FocusError />

                      <Row>
                        <Col lg="12" className="col-lg-12">
                          <InputField
                            className="floating-label"
                            type="password"
                            name="password"
                            placeholder="***********"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            showPasswordToggle={true}
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                            asterick={true}
                            errorMessage={errorPassword}
                            label="Password"
                          />
                          <InputField
                            className="floating-label"
                            type="password"
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            showPasswordToggle={true}
                            showPassword={showConfirmPassword}
                            setShowPassword={setShowConfirmPassword}
                            asterick={true}
                            errorMessage={errorConfirmPassword}
                            label="Confirm Password"
                          />
                        </Col>
                      </Row>
                      <Button
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                        className="mt-3"
                        type="button"
                        variant="primary"
                      >
                        Submit
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
            <div className="sign-bg sign-bg-right">
              <svg
                width="280"
                height="230"
                viewBox="0 0 431 398"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g opacity="0.05">
                  <rect
                    x="-157.085"
                    y="193.773"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(-45 -157.085 193.773)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="7.46875"
                    y="358.327"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(-45 7.46875 358.327)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="61.9355"
                    y="138.545"
                    width="310.286"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(45 61.9355 138.545)"
                    fill="#3B8AFF"
                  />
                  <rect
                    x="62.3154"
                    y="-190.173"
                    width="543"
                    height="77.5714"
                    rx="38.7857"
                    transform="rotate(45 62.3154 -190.173)"
                    fill="#3B8AFF"
                  />
                </g>
              </svg>
            </div>
          </Col>
        </Row>
      </section>
    </>
  );
};

export default RecoverPassword;
